/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faHandshake, 
  faComments,
  faArrowLeft,
  faGlobe,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEmployerName } from '../utils/nameFormatter';
import bapsLogo from '../assets/BAPS.png';
import CustomTooltip from '../components/CustomTooltip';
import '../styles/Companies.css';
import '../styles/Communities.css';

const Communities = () => {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCommunities = async () => {
      try {
        setLoading(true);
        const response = await axios.get(buildApiUrl('/api/companies/list'));
        const allCompanies = response.data?.companies || [];
        
        // Filter communities
        const filtered = allCompanies.filter(company => 
          (company.section || '').toLowerCase() === 'communities'
        );
        setCommunities(filtered);
      } catch (error) {
        console.error('❌ Error fetching communities:', error);
        setCommunities([]);
      } finally {
        setLoading(false);
      }
    };

    loadCommunities();
  }, []);

  const stats = useMemo(() => {
    // Include BAPS Swaminarayan Mandir (hardcoded) + communities from API
    const totalPartners = communities.length + 1; // +1 for BAPS
    const totalDiscussions = communities.reduce((count, community) => community.focusAreas ? count + 1 : count, 0);
    const totalCollaborations = communities.reduce((count, community) => community.memberCount ? count + 1 : count, 0);
    
    return {
      partners: totalPartners,
      discussions: totalDiscussions,
      collaborations: totalCollaborations
    };
  }, [communities]);

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="companies-page communities-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faUsers} className="badge-icon" />
            <span>COMMUNITY NETWORK</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Vibrant Communities <br />
            <span className="gradient-text">Building Connections Together</span>
          </h1>
          <p className="companies-hero-subtitle">
            Join dynamic professional communities and organizations actively engaging to create meaningful career opportunities
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.partners}+</div>
                <div className="stat-label">Partner Communities</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faComments} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.discussions}+</div>
                <div className="stat-label">Active Discussions</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.collaborations}+</div>
                <div className="stat-label">Collaborations</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      {/* Community Partners Section - Moved above NGO section */}
      <section className="companies-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        <div style={{ marginBottom: '2rem', maxWidth: '760px', margin: '0 auto 2rem auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', textAlign: 'center' }}>
            Our Community Partners
          </h2>
          <p style={{ color: '#4b5563', fontSize: '16px', margin: 0, textAlign: 'center' }}>
            Faith-based, cultural, and professional communities partnering with Akshar Jobs.
          </p>
        </div>

        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading communities...</p>
          </div>
        ) : (
          <div className="companies-grid">
            {/* BAPS AFRICA - Featured Community */}
            <CustomTooltip text="Bochasanwasi Shri Akshar Purushottam Swaminarayan Sanstha" position="top">
              <div 
                className="company-card" 
                key="baps-africa"
                style={{ cursor: 'pointer' }}
              >
              <div className="company-card-header">
                <div className="card-header-bg"></div>
                <div className="company-logo-wrapper">
                  <img
                    src={bapsLogo}
                    alt="BAPS AFRICA"
                    className="company-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="company-logo-placeholder"
                    style={{ display: 'none' }}
                  >
                    <span className="initials-text">BAPS</span>
                  </div>
                </div>
                <div className="verified-badge">
                  <FontAwesomeIcon icon={faHandshake} />
                  <span>Verified</span>
                </div>
              </div>

              <div className="company-card-body">
                <h3 className="company-name" style={{ textAlign: 'center' }}>BAPS AFRICA</h3>
                <div className="company-detail" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span style={{ flex: 'none' }}>Nairobi, Kenya</span>
                </div>
              </div>
              </div>
            </CustomTooltip>

            {communities.map((community, index) => {
              const logo = community.logo || community.companyLogo || '';
              const rawName = community.companyName || community.communityName || community.employerName || 'Community';
              const name = formatEmployerName(rawName);
              const location = community.address || community.country || '';
              const description = community.focusAreas || community.aboutUs || '';
              const website = community.website;

              return (
                <CustomTooltip text={name} position="top" key={community._id || community.id || index}>
                  <div 
                    className="company-card" 
                    style={{ cursor: 'pointer' }}
                  >
                  <div className="company-card-header">
                    <div className="card-header-bg"></div>
                    <div className="company-logo-wrapper">
                      {logo ? (
                        <img
                          src={logo}
                          alt={name}
                          className="company-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="company-logo-placeholder"
                        style={{ display: logo ? 'none' : 'flex' }}
                      >
                        <span className="initials-text">{getInitials(name)}</span>
                      </div>
                    </div>
                    <div className="verified-badge">
                      <FontAwesomeIcon icon={faHandshake} />
                      <span>Community Partner</span>
                    </div>
                  </div>

                  <div className="company-card-body">
                    <h3 className="company-name" style={{ textAlign: 'center' }}>{name}</h3>
                    {location && (
                      <div className="company-detail" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span style={{ flex: 'none' }}>{location}</span>
                      </div>
                    )}
                    {community.memberCount && (
                      <div className="company-detail" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <FontAwesomeIcon icon={faUsers} />
                        <span style={{ flex: 'none' }}>{community.memberCount}+ members</span>
                      </div>
                    )}
                    {description && (
                      <p className="company-description">
                        {description.length > 140 ? `${description.slice(0, 140)}…` : description}
                      </p>
                    )}
                  </div>
                  </div>
                </CustomTooltip>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Communities;

